import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Baby, Calendar, AlertTriangle, Grid3X3, List, Download } from 'lucide-react';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useSettings } from '@/contexts/SettingsContext';
import { BirthFormDialog } from '@/components/dialogs/BirthFormDialog';
import { BirthTableView } from '@/components/naissances/BirthTableView';
import { BirthCardsView } from '@/components/naissances/BirthCardsView';
import { BirthSurvivalStats } from '@/components/naissances/BirthSurvivalStats';
import { BirthEditDialog } from '@/components/naissances/BirthEditDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Naissances = ({ user }) => {
  const { isMobile } = useScreenSize();
  const { settings } = useSettings();
  const itemsPerPage = useMemo(() => settings.items_per_page || 10, [settings.items_per_page]);
  const [births, setBirths] = useState([]);
  const [sheep, setSheep] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBirth, setSelectedBirth] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('date-desc');
  const [filterStatus, setFilterStatus] = useState('tous');
  const [breeds, setBreeds] = useState([]);
  const [creerAgneaux, setCreerAgneaux] = useState(false);
  const [agneauxData, setAgneauxData] = useState([]);
  const [formData, setFormData] = useState({ mere_id: '', pere_id: '', date_prevue: '', nombre_agneaux: 1, notes: '' });
  const [editFormData, setEditFormData] = useState({ date_reelle: '', statut: 'prevue', notes: '', agneaux_morts: 0, agneaux_ids: [] });
  const [editCreerAgneaux, setEditCreerAgneaux] = useState(false);
  const [editAgneauxData, setEditAgneauxData] = useState([]);

  const canWrite = user?.permissions?.includes('write') || user?.role === 'admin';
  const canDelete = user?.permissions?.includes('delete') || user?.role === 'admin';
  const getSheepById = useCallback((id) => sheep.find((s) => s.id === id), [sheep]);
  const femelles = sheep.filter(s => s.sexe === 'femelle' && s.statut === 'actif');
  const males = sheep.filter(s => s.sexe === 'male' && s.statut === 'actif');

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (isMobile && viewMode === 'table') setViewMode('cards'); }, [isMobile, viewMode]);
  useEffect(() => { setCurrentPage(1); }, [sortBy, filterStatus]);

  const filteredBirths = useMemo(() => {
    let filtered = [...births];
    if (filterStatus !== 'tous') filtered = filtered.filter(b => b.statut === filterStatus);
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc': return new Date(b.date_prevue) - new Date(a.date_prevue);
        case 'date-asc': return new Date(a.date_prevue) - new Date(b.date_prevue);
        case 'statut': return a.statut.localeCompare(b.statut);
        case 'mere':
          const mereA = getSheepById(a.mere_id);
          const mereB = getSheepById(b.mere_id);
          return (mereA?.numero || '').localeCompare(mereB?.numero || '');
        default: return new Date(b.date_prevue) - new Date(a.date_prevue);
      }
    });
    return filtered;
  }, [births, sheep, sortBy, filterStatus, getSheepById]);

  const totalPages = Math.ceil(filteredBirths.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedBirths = filteredBirths.slice(startIndex, startIndex + itemsPerPage);
  const totalBirths = births.length;
  const upcomingBirths = births.filter(b => b.statut === 'prevue' && new Date(b.date_prevue) >= new Date()).length;
  const completedBirths = births.filter(b => b.statut === 'ne').length;
  const overdueBirths = births.filter(b => b.statut === 'prevue' && new Date(b.date_prevue) < new Date()).length;

  const birthStats = useMemo(() => {
    const naissancesTerminees = births.filter(b => b.statut === 'ne');
    const allAgneauIds = naissancesTerminees.filter(b => b.agneaux_ids && b.agneaux_ids.length > 0).flatMap(b => b.agneaux_ids);
    const agneauxMortsDeclares = naissancesTerminees.reduce((sum, b) => sum + (b.agneaux_morts || 0), 0);
    const totalAgneaux = allAgneauIds.length + agneauxMortsDeclares;
    const agneaux = sheep.filter(s => allAgneauIds.includes(s.id));
    const agneauxMortsCrees = agneaux.filter(a => a.statut === 'mort').length;
    const agneauxVivants = agneaux.filter(a => a.statut !== 'mort').length;
    const totalMorts = agneauxMortsCrees + agneauxMortsDeclares;
    const tauxSurvie = totalAgneaux > 0 ? ((agneauxVivants / totalAgneaux) * 100).toFixed(1) : 0;
    return { totalAgneaux, agneauxVivants, agneauxMorts: totalMorts, tauxSurvie: parseFloat(tauxSurvie), naissancesCount: naissancesTerminees.length };
  }, [births, sheep]);

  const fetchData = useCallback(async () => {
    try {
      const [birthsRes, sheepRes, breedsRes] = await Promise.all([
        axios.get(`${API}/births`), axios.get(`${API}/sheep`), axios.get(`${API}/breeds`),
      ]);
      setBirths(birthsRes.data); setSheep(sheepRes.data); setBreeds(breedsRes.data);
    } catch { toast.error('Erreur lors du chargement des données'); }
    finally { setLoading(false); }
  }, []);

  const getNextSheepNumber = () => {
    if (sheep.length === 0) return '001';
    const numbers = sheep.map(s => parseInt(s.numero) || 0);
    return String(Math.max(...numbers) + 1).padStart(3, '0');
  };

  const initializeAgneaux = async (count) => {
    try {
      const sheepRes = await axios.get(`${API}/sheep`);
      const currentSheep = sheepRes.data;
      let baseNumber = 1;
      if (currentSheep.length > 0) { baseNumber = Math.max(...currentSheep.map(s => parseInt(s.numero) || 0)) + 1; }
      const mere = currentSheep.find(s => s.id === formData.mere_id);
      setAgneauxData(Array.from({ length: count }, (_, i) => ({
        numero: String(baseNumber + i).padStart(3, '0'), nom: '', sexe: 'femelle', race: mere?.race || '', poids: '', notes: '',
      })));
    } catch {
      const baseNumber = parseInt(getNextSheepNumber()) || 1;
      const mere = getSheepById(formData.mere_id);
      setAgneauxData(Array.from({ length: count }, (_, i) => ({
        numero: String(baseNumber + i).padStart(3, '0'), nom: '', sexe: 'femelle', race: mere?.race || '', poids: '', notes: '',
      })));
    }
  };

  const updateAgneau = (index, field, value) => {
    const updated = [...agneauxData];
    updated[index] = { ...updated[index], [field]: value };
    setAgneauxData(updated);
  };

  const initializeEditAgneaux = async (count, birth) => {
    try {
      const sheepRes = await axios.get(`${API}/sheep`);
      const currentSheep = sheepRes.data;
      let baseNumber = 1;
      if (currentSheep.length > 0) { baseNumber = Math.max(...currentSheep.map(s => parseInt(s.numero) || 0)) + 1; }
      const mere = currentSheep.find(s => s.id === birth?.mere_id);
      setEditAgneauxData(Array.from({ length: count }, (_, i) => ({
        numero: String(baseNumber + i).padStart(3, '0'), nom: '', sexe: 'femelle', race: mere?.race || '', poids: '', notes: '',
      })));
    } catch {
      const baseNumber = parseInt(getNextSheepNumber()) || 1;
      const mere = getSheepById(birth?.mere_id);
      setEditAgneauxData(Array.from({ length: count }, (_, i) => ({
        numero: String(baseNumber + i).padStart(3, '0'), nom: '', sexe: 'femelle', race: mere?.race || '', poids: '', notes: '',
      })));
    }
  };

  const updateEditAgneau = (index, field, value) => {
    const updated = [...editAgneauxData];
    updated[index] = { ...updated[index], [field]: value };
    setEditAgneauxData(updated);
  };

  const resetForm = () => {
    setFormData({ mere_id: '', pere_id: '', date_prevue: '', nombre_agneaux: 1, notes: '' });
    setCreerAgneaux(false); setAgneauxData([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) { toast.error("Vous n'avez pas la permission"); return; }
    try {
      const data = { ...formData, nombre_agneaux: parseInt(formData.nombre_agneaux) || 1, creer_agneaux: creerAgneaux,
        agneaux: creerAgneaux ? agneauxData.map(a => ({ ...a, poids: a.poids ? parseFloat(a.poids) : null })) : null };
      const response = await axios.post(`${API}/births`, data);
      toast.success(response.data.agneaux_crees > 0 ? `Naissance enregistrée avec ${response.data.agneaux_crees} agneau(x) créé(s)` : 'Naissance ajoutée avec succès');
      fetchData(); setDialogOpen(false); resetForm();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!canWrite) { toast.error("Vous n'avez pas la permission"); return; }
    try {
      const cleanedFormData = {
        statut: editFormData.statut, notes: editFormData.notes || null,
        agneaux_morts: editFormData.statut === 'ne' ? (editFormData.agneaux_morts || 0) : 0,
        agneaux_ids: editFormData.agneaux_ids || [],
      };
      if (editFormData.date_reelle && editFormData.date_reelle.trim() !== '') cleanedFormData.date_reelle = editFormData.date_reelle;

      if (editFormData.statut === 'ne' && editCreerAgneaux && editAgneauxData.length > 0) {
        const dateNaissance = cleanedFormData.date_reelle || selectedBirth.date_prevue;
        const agneauxIds = [];
        for (const agneau of editAgneauxData) {
          const sheepData = { numero: agneau.numero, nom: agneau.nom || '', date_naissance: dateNaissance, race: agneau.race, sexe: agneau.sexe,
            poids: agneau.poids ? parseFloat(agneau.poids) : null, statut: 'actif', origine: 'naissance',
            notes: agneau.notes || `Ne(e) le ${dateNaissance}`, mere_id: selectedBirth.mere_id, pere_id: selectedBirth.pere_id || null };
          const response = await axios.post(`${API}/sheep`, sheepData);
          agneauxIds.push(response.data.id);
        }
        await axios.put(`${API}/births/${selectedBirth.id}`, { ...cleanedFormData, agneaux_ids: [...(selectedBirth.agneaux_ids || []), ...agneauxIds] });
        toast.success(`Naissance mise à jour avec ${agneauxIds.length} agneau(x) créé(s)`);
      } else {
        await axios.put(`${API}/births/${selectedBirth.id}`, cleanedFormData);
        toast.success('Naissance modifiée avec succès');
      }
      fetchData(); setEditDialogOpen(false); setEditCreerAgneaux(false); setEditAgneauxData([]);
    } catch { toast.error('Erreur lors de la modification'); }
  };

  const handleDelete = async (id) => {
    if (!canDelete) { toast.error("Vous n'avez pas la permission de supprimer"); return; }
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette naissance ?')) {
      try { await axios.delete(`${API}/births/${id}`); toast.success('Naissance supprimée'); fetchData(); }
      catch { toast.error('Erreur lors de la suppression'); }
    }
  };

  const openEditDialog = (birth) => {
    setSelectedBirth(birth);
    setEditFormData({ date_reelle: birth.date_reelle || '', statut: birth.statut, notes: birth.notes || '', agneaux_morts: birth.agneaux_morts || 0, agneaux_ids: birth.agneaux_ids || [] });
    setEditCreerAgneaux(false); setEditAgneauxData([]);
    setEditDialogOpen(true);
  };

  const exportToPDF = () => {
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Rapport Naissances</title><style>body{font-family:Arial,sans-serif;margin:20px;color:#333}.header{text-align:center;margin-bottom:30px}table{width:100%;border-collapse:collapse;margin-top:20px}th,td{border:1px solid #ddd;padding:12px;text-align:left}th{background-color:#334155;color:white}.completed{background-color:#f0fdf4}.overdue{background-color:#fef2f2}</style></head><body><div class="header"><h2>Rapport des Naissances</h2><p><strong>Genere le:</strong> ${new Date().toLocaleDateString('fr-FR')}</p></div><table><thead><tr><th>Mere</th><th>Date prevue</th><th>Date reelle</th><th>Statut</th><th>Notes</th></tr></thead><tbody>${filteredBirths.map(birth => { const mere = getSheepById(birth.mere_id); return `<tr class="${birth.statut === 'ne' ? 'completed' : birth.statut === 'prevue' && new Date(birth.date_prevue) < new Date() ? 'overdue' : ''}"><td>N\u00b0 ${mere?.numero || 'Inconnue'} ${mere?.nom || ''}</td><td>${new Date(birth.date_prevue).toLocaleDateString('fr-FR')}</td><td>${birth.date_reelle ? new Date(birth.date_reelle).toLocaleDateString('fr-FR') : '-'}</td><td>${birth.statut === 'prevue' ? 'Prevue' : birth.statut === 'ne' ? 'Nee' : birth.statut}</td><td>${birth.notes || '-'}</td></tr>`; }).join('')}</tbody></table></body></html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
    const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `rapport_naissances_${new Date().toISOString().split('T')[0]}.html`; link.click(); URL.revokeObjectURL(link.href);
    toast.success('Rapport naissances généré');
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 dark:border-slate-400"></div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="naissances-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Gestion des Naissances</h1>
        <div className="flex gap-2">
          <Button onClick={exportToPDF} variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400" data-testid="export-naissances-button"><Download className="w-4 h-4 mr-2" />Export PDF</Button>
          {canWrite && <Button onClick={() => setDialogOpen(true)} className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600" data-testid="add-birth-button"><Plus className="w-4 h-4 mr-2" />Déclarer une naissance</Button>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md border-l-4 border-l-blue-500"><CardContent className="pt-6"><div className="flex items-center"><Calendar className="w-8 h-8 text-blue-600 dark:text-blue-400" /><div className="ml-4"><p className="text-sm font-medium text-gray-600 dark:text-gray-400">À venir</p><p className="text-2xl font-bold text-blue-600">{upcomingBirths}</p></div></div></CardContent></Card>
        <Card className="border-0 shadow-md border-l-4 border-l-green-500"><CardContent className="pt-6"><div className="flex items-center"><Baby className="w-8 h-8 text-green-600 dark:text-green-400" /><div className="ml-4"><p className="text-sm font-medium text-gray-600 dark:text-gray-400">Terminées</p><p className="text-2xl font-bold text-green-600">{completedBirths}</p></div></div></CardContent></Card>
        <Card className="border-0 shadow-md border-l-4 border-l-red-500"><CardContent className="pt-6"><div className="flex items-center"><AlertTriangle className="w-8 h-8 text-red-600" /><div className="ml-4"><p className="text-sm font-medium text-gray-600 dark:text-gray-400">En retard</p><p className="text-2xl font-bold text-red-600">{overdueBirths}</p></div></div></CardContent></Card>
        <Card className="border-0 shadow-md border-l-4 border-l-gray-500"><CardContent className="pt-6"><div className="flex items-center"><Baby className="w-8 h-8 text-gray-600" /><div className="ml-4"><p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p><p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{totalBirths}</p></div></div></CardContent></Card>
      </div>

      <BirthSurvivalStats birthStats={birthStats} />

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2"><Label>Filtrer par statut</Label><Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="tous">Toutes les naissances</SelectItem><SelectItem value="prevue">Prévues uniquement</SelectItem><SelectItem value="ne">Terminées uniquement</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Trier par</Label><Select value={sortBy} onValueChange={setSortBy}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="date-desc">Date prévue (récente)</SelectItem><SelectItem value="date-asc">Date prévue (ancienne)</SelectItem><SelectItem value="statut">Statut</SelectItem><SelectItem value="mere">Mère (numéro)</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Affichage</Label><div className="flex gap-1">{!isMobile && <Button type="button" variant={viewMode === 'table' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('table')}><List className="w-4 h-4" /></Button>}<Button type="button" variant={viewMode === 'cards' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('cards')}><Grid3X3 className="w-4 h-4" /></Button></div></div>
            <div className="space-y-2"><Label>Résultats</Label><div className="h-10 flex items-center px-3 bg-gray-50 dark:bg-gray-950 rounded-lg border"><span className="text-sm font-medium text-gray-700 dark:text-gray-300">{filteredBirths.length} naissance(s)</span></div></div>
          </div>
        </CardContent>
      </Card>

      {viewMode === 'table' && <BirthTableView paginatedBirths={paginatedBirths} getSheepById={getSheepById} canWrite={canWrite} canDelete={canDelete} openEditDialog={openEditDialog} handleDelete={handleDelete} currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} startIndex={startIndex} itemsPerPage={itemsPerPage} totalItems={filteredBirths.length} />}
      {viewMode === 'cards' && <BirthCardsView paginatedBirths={paginatedBirths} getSheepById={getSheepById} sheep={sheep} canWrite={canWrite} canDelete={canDelete} openEditDialog={openEditDialog} handleDelete={handleDelete} currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} startIndex={startIndex} itemsPerPage={itemsPerPage} totalItems={filteredBirths.length} />}

      <BirthFormDialog open={dialogOpen} onOpenChange={setDialogOpen} formData={formData} setFormData={setFormData} onSubmit={handleSubmit} femelles={femelles} males={males} breeds={breeds} sheep={sheep} creerAgneaux={creerAgneaux} setCreerAgneaux={setCreerAgneaux} agneauxData={agneauxData} setAgneauxData={setAgneauxData} updateAgneau={updateAgneau} initializeAgneaux={initializeAgneaux} />

      <BirthEditDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} selectedBirth={selectedBirth} editFormData={editFormData} setEditFormData={setEditFormData} onSubmit={handleUpdate} sheep={sheep} breeds={breeds} editCreerAgneaux={editCreerAgneaux} setEditCreerAgneaux={setEditCreerAgneaux} editAgneauxData={editAgneauxData} setEditAgneauxData={setEditAgneauxData} initializeEditAgneaux={initializeEditAgneaux} updateEditAgneau={updateEditAgneau} />
    </div>
  );
};

export default Naissances;
